<template>
  <validation-observer ref="observer" v-slot="{ invalid }">
    <form @submit.prevent="submit" v-if="shaft">
      <v-expansion-panels
        flat
        tile
        class="channels-list"
        v-model="selectedSideChannel"
      >
        <v-expansion-panel
          v-for="(item, index) in shaft.side_channels"
          :key="item.id"
          class="mb-1"
          @change="onExpandedItemChanged"
        >
          <v-expansion-panel-header class="grey lighten-3">
            <div
              class="pa-0 ma-0 v-list-item__title d-flex flex-row align-center"
            >
              <h4>
                Seitenzulauf
                {{ item.diameter ? `DN${item.diameter.dn}` : item.name }}
                {{ item.angle ? item.angle + '°' : '' }}
              </h4>
              <v-icon
                color="primary"
                small
                class="ml-2"
                v-if="invalid && item.invalid"
                >mdi-alert-circle-outline</v-icon
              >
              <v-btn
                fab
                depressed
                x-small
                outlined
                color="primary"
                class="delete-button"
                @click.stop.prevent="deleteChannelClick(item)"
                v-if="!shaft.ordered"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-6">
            <validation-provider
              v-slot="{ errors }"
              name="dn"
              rules="required"
              mode="lazy"
              :vid="'diameter' + index"
            >
              <v-select
                v-model="item.diameter"
                :items="channelChoices"
                :error-messages="errors"
                label="Durchmesser *"
                data-vv-name="dn"
                item-text="dn"
                item-value="id"
                return-object
                outlined
                color="grey darken-2"
                :disabled="shaft.ordered"
                @change="updateSubmaterials(item)"
              >
              </v-select>
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              name="material"
              rules="required"
              mode="lazy"
              :vid="'material' + index"
            >
              <v-select
                v-model="item.material"
                :items="item.subMaterialChoices"
                :error-messages="errors"
                label="Material *"
                data-vv-name="material"
                item-text="material"
                item-value="material"
                outlined
                color="grey darken-2"
                :disabled="shaft.ordered"
              >
              </v-select>
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              name="title"
              rules="max:255"
              mode="lazy"
              :vid="'note' + index"
            >
              <v-text-field
                v-model="item.note"
                :error-messages="errors"
                label="Fabrikat"
                outlined
                color="grey darken-2"
                :disabled="shaft.ordered"
              ></v-text-field>
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              name="angle"
              rules="required|integer"
              mode="lazy"
              :vid="'angle' + index"
            >
              <v-text-field
                v-model="item.angle"
                :error-messages="errors"
                label="Winkel *"
                suffix="°"
                outlined
                color="grey darken-2"
                class="angle-input"
                :disabled="shaft.ordered"
              >
                <template v-slot:append-outer>
                  <DialogAngleInput
                    :shaft="shaft"
                    :start-angle="item.angle"
                    title="Seitenzulauf"
                    type="side_channel"
                    @angleChanged="angle => $set(item, 'angle', angle)"
                    :disabled="shaft.ordered"
                  />
                </template>
              </v-text-field>
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              name="tilt"
              rules="integer"
              mode="lazy"
              :vid="'tilt' + index"
            >
              <v-text-field
                v-model="item.tilt"
                :error-messages="errors"
                label="Muffenschrägstellung"
                suffix="‰"
                outlined
                color="grey darken-2"
                :disabled="shaft.ordered"
              ></v-text-field>
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              name="alignment"
              mode="lazy"
              :vid="'alignment' + index"
            >
              <v-select
                v-model="item.alignment"
                :items="alignmentChoices"
                :error-messages="errors"
                label="Scheitel"
                data-vv-name="alignment"
                item-text="text"
                item-value="value"
                outlined
                color="grey darken-2"
                :disabled="shaft.ordered"
              >
              </v-select>
            </validation-provider>
            <validation-provider
              v-slot="{ errors }"
              name="offset"
              rules="integer"
              mode="lazy"
              :vid="'offset' + index"
            >
              <v-text-field
                v-model="item.offset"
                :error-messages="errors"
                v-if="!offsetDisabled(item)"
                label="Höher oder Tiefer"
                suffix="cm"
                outlined
                color="grey darken-2"
                :disabled="shaft.ordered"
              ></v-text-field>
            </validation-provider>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-list-item
        class="add-item grey lighten-3"
        @click="addChannelClick"
        v-if="!shaft.ordered"
      >
        <v-list-item-content>
          <v-list-item-title class="text-center">
            <v-icon large color="primary" class="mr-2">mdi-plus-circle </v-icon>
            <span>Seitenzulauf hinzufügen</span></v-list-item-title
          >
        </v-list-item-content>
      </v-list-item>
      <div class="my-4 text-center">
        <v-btn
          color="primary"
          class="text-center"
          type="submit"
          :loading="isSubmitting"
          :disabled="invalid"
          outlined
        >
          Speichern
        </v-btn>
      </div>
    </form>
    <DialogConfirm
      ref="deleteDialog"
      caption="Seitenzulauf wirklich"
      title="löschen?"
      message="Diese Aktion kann nicht mehr rückgängig gemacht werden."
      ok="Ja, löschen"
      cancel="Nein"
    />
  </validation-observer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DialogAngleInput from '@/components/DialogAngleInput.vue';
import DialogConfirm from '@/components/DialogConfirm.vue';

export default {
  name: 'FormEditShaft4',
  components: { DialogAngleInput, DialogConfirm },
  props: ['shaft', 'alignmentChoices', 'materialChoices'],
  data: () => ({
    isSubmitting: false,
    selectedSideChannel: -1,
    isValid: true,
  }),
  computed: {
    ...mapGetters([]),
    channelChoices() {
      if (this.shaft.type && this.shaft.type.channel_options)
        return this.shaft.type.channel_options;
      return [];
    },
  },
  mounted() {
    this.$watch(
      () => {
        return this.$refs.observer.errors;
      },
      () => {
        this.checkSideChannelForms();
      },
    );
  },
  methods: {
    ...mapActions(['updateCurrentShaft']),
    onExpandedItemChanged() {
      // validate forms
      this.$refs.observer.validate();

      // update materials
      if (this.shaft && this.shaft.side_channels) {
        this.shaft.side_channels.forEach(sideChannel => {
          this.updateSubmaterials(sideChannel);
        });
      }
    },
    submit() {
      this.$refs.observer.validate().then(async success => {
        if (!success) {
          return;
        }
        this.isSubmitting = true;
        try {
          if (!this.shaft.ordered) await this.updateCurrentShaft(this.shaft);
          this.$emit('submit');
        } catch {
          // ignored
        }
        this.isSubmitting = false;
      });
    },
    clear() {
      this.$refs.observer.reset();
    },
    addChannelClick() {
      if (!this.shaft.side_channels) this.shaft.side_channels = [];
      this.shaft.side_channels.push({ name: 'Neu' });
      this.selectedSideChannel = this.shaft.side_channels.length - 1;
    },
    async deleteChannelClick(item) {
      var result = await this.$refs.deleteDialog.show();
      if (result) {
        var index = this.shaft.side_channels.indexOf(item);
        if (index === -1) return;
        this.shaft.side_channels.splice(index, 1);
      }
    },
    offsetDisabled(item) {
      return !(item && item.alignment === 'Höher/Tiefer');
    },
    updateSubmaterials(item) {
      // grab materials that are possible with the chosen diameter
      if (item.diameter) {
        item.subMaterialChoices = [];
        this.materialChoices.forEach(material => {
          if (material.diameters.includes('' + item.diameter.dn))
            item.subMaterialChoices.push(material);
        });
      }

      // check if current material is still in list, otherwise reset
      if (item.material) {
        var index = item.subMaterialChoices.findIndex(
          material => material.material === item.material,
        );
        if (index < 0) item.material = undefined;
      }
    },
    checkSideChannelForms() {
      if (this.shaft && this.shaft.side_channels) {
        this.shaft.side_channels.forEach(sideChannel => {
          sideChannel.invalid = false;
          const index = this.shaft.side_channels.indexOf(sideChannel);
          Object.keys(this.$refs.observer.errors).forEach(error => {
            if (
              error.indexOf('' + index) >= 0 &&
              this.$refs.observer.errors[error].length > 0
            )
              sideChannel.invalid = true;
          });
        });
      }
    },
  },
};
</script>

<style scoped>
.angle-input >>> .v-input__append-outer {
  margin-top: 0;
  min-height: 56px;
}

.angle-input >>> .v-input__append-outer button {
  height: auto;
}

.v-expansion-panel-header--active >>> .v-expansion-panel-header__icon {
  display: none;
}

.delete-button {
  position: absolute;
  right: 20px;
}

.v-expansion-panel-header >>> .delete-button {
  display: none;
}

.v-expansion-panel-header--active >>> .delete-button {
  display: block;
}
</style>
